/// <amd-module name="Repos/Views/Settings/Options/Components/SearchableBranches" />
import React = require("react");
import { GitRepository } from "Repos/Common/Contracts/Git";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import * as Resources from "Repos/Views/Settings/Options/Resources";
import { IObservableArray, IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import * as Utils from "VSS/Core/Util/String";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dialog } from "VSSUI/Dialog";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { IBranch } from "../Contracts/Contracts";
import { IOptionsViewService } from "../Services/OptionsViewService";
import { SearchableBranchesTable } from "./SearchableBranchesTable";

interface ISearchableBranchesProps {
    defaultBranch?: string;
    projectId: string;
    repository: GitRepository;
}

export class SearchableBranches extends VssComponent<ISearchableBranchesProps, {}> {
    private branchToAdd: IObservableValue<IBranch | undefined>;
    private branchToRemove: IObservableValue<IBranch | undefined>;
    private data: IObservableArray<IBranch | IReadonlyObservableValue<IBranch | undefined>>;
    private failureMessage: IObservableValue<string | undefined>;
    private isDialogAddOpen: IObservableValue<boolean>;
    private isDialogRemoveOpen: IObservableValue<boolean>;
    private maxCount: number;
    private viewSvc: IOptionsViewService;

    public constructor(props: ISearchableBranchesProps, context: IVssComponentContext) {
        super(props, context);
        this.viewSvc = context.pageContext.getService<IOptionsViewService>("OptionsViewService");
        this.data = this.viewSvc.getSearchableBranches(this.props.projectId, this.props.repository.id, this.props.defaultBranch);
        this.maxCount = this.viewSvc.getSearchableBranchesMaxCount();
        this.isDialogAddOpen = new ObservableValue<boolean>(false);
        this.isDialogRemoveOpen = new ObservableValue<boolean>(false);
        this.branchToAdd = new ObservableValue<IBranch | undefined>(undefined);
        this.branchToRemove = new ObservableValue<IBranch | undefined>(undefined);
        this.failureMessage = new ObservableValue<string | undefined>(undefined);
    }

    public render = (): JSX.Element => {
        return (
            <>
                <Observer data={this.data}>
                    {(props: { data: any }) => {
                        return (
                            <Card
                                titleProps={{ text: Resources.BranchCardName }}
                                headerDescriptionProps={{ text: Utils.format(Resources.BranchCardDescription, this.maxCount) }}
                                headerCommandBarItems={this.getCommandBarItems()}
                            >
                                <SearchableBranchesTable data={this.data} onRemoveClick={this.showDialogRemove}></SearchableBranchesTable>
                            </Card>
                        );
                    }}
                </Observer>
                <ConditionalChildren renderChildren={this.isDialogRemoveOpen}>
                    <Dialog
                        titleProps={{ text: Resources.RemoveBranchAria }}
                        footerButtonProps={[
                            {
                                text: Resources.Cancel,
                                onClick: this.hideDialogRemove
                            },
                            {
                                text: Resources.RemoveBranchAria,
                                onClick: this.onRemove,
                                primary: true
                            }
                        ]}
                        onDismiss={this.hideDialogRemove}
                    >
                        <Observer branchToRemove={this.branchToRemove}>
                            {() => {
                                if (this.branchToRemove.value) {
                                    return (
                                        <span>
                                            {Resources.RemoveBranchDescriptionPart1}
                                            <span className="font-weight-heavy">{this.branchToRemove.value.name}</span>
                                            {Resources.RemoveBranchDescriptionPart2}
                                        </span>
                                    );
                                }
                            }}
                        </Observer>
                    </Dialog>
                </ConditionalChildren>
                <ConditionalChildren renderChildren={this.isDialogAddOpen}>
                    <Observer branchToAdd={this.branchToAdd} failureMessage={this.failureMessage}>
                        {(props: { branchToAdd: IBranch | undefined }) => {
                            return (
                                <Dialog
                                    titleProps={{ text: Resources.AddBranchDescription }}
                                    footerButtonProps={[
                                        {
                                            text: Resources.Cancel,
                                            onClick: this.hideDialogAdd
                                        },
                                        {
                                            text: Resources.AddBranch,
                                            onClick: this.onAdd,
                                            primary: true,
                                            disabled: !this.branchToAdd.value || !!this.failureMessage.value
                                        }
                                    ]}
                                    onDismiss={this.hideDialogAdd}
                                >
                                    <div className="flex-column rhythm-vertical-16">
                                        {this.failureMessage.value && (
                                            <MessageCard severity={MessageCardSeverity.Error} className="flex-noshrink">
                                                <div className="flex-column font-size-ms">{this.failureMessage.value}</div>
                                            </MessageCard>
                                        )}
                                        <div className="flex-row">
                                            <VersionDropdown
                                                placeholder={Resources.AddBranchPlaceholder}
                                                onSelect={this.onSelectBranchToAdd}
                                                providerOptions={{
                                                    repository: this.props.repository,
                                                    viewTagsPivot: false,
                                                    hideFavoriteSelectedItemIndicator: true
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Dialog>
                            );
                        }}
                    </Observer>
                </ConditionalChildren>
            </>
        );
    };

    private getCommandBarItems = (): IHeaderCommandBarItem[] => {
        return [
            {
                important: true,
                id: "add",
                onActivate: this.showDialogAdd,
                iconProps: {
                    iconName: "Add"
                },
                tooltipProps: {
                    text: Resources.AddBranch
                },
                ariaLabel: Resources.AddBranch,
                disabled:
                    !this.data ||
                    this.data.length === 0 ||
                    !isIBranch(this.data.value[0]) ||
                    this.data.length >= this.maxCount + (this.props.defaultBranch ? 1 : 0)
            }
        ];
    };

    private hideDialogAdd = () => {
        this.isDialogAddOpen.value = false;
        this.branchToAdd.value = undefined;
        this.failureMessage.value = undefined;
    };

    private hideDialogRemove = () => {
        this.isDialogRemoveOpen.value = false;
        this.branchToRemove.value = undefined;
    };

    private onAdd = () => {
        if (this.branchToAdd.value) {
            const branches = [...(this.data.value as IBranch[])];
            branches.push(this.branchToAdd.value);

            this.viewSvc.updateSearchableBranches(this.props.projectId, this.props.repository.id, branches).then(data => {
                this.data.value = data;
                this.hideDialogAdd();
            });
        }
    };

    private onRemove = () => {
        if (this.branchToRemove.value) {
            const updatedBranches = this.data.value.filter(branch => branch != this.branchToRemove.value) as IBranch[];
            this.viewSvc.updateSearchableBranches(this.props.projectId, this.props.repository.id, updatedBranches).then(data => {
                this.data.value = data;
                this.hideDialogRemove();
            });
        }
    };

    private onSelectBranchToAdd = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null) => {
        if (item && item.data) {
            const branch: IBranch = { name: item.data.friendlyName, ref: branchNameToRef(item.data.friendlyName) };
            this.branchToAdd.value = branch;

            if (this.data.value.find(item => item && (item as IBranch).ref == branch.ref)) {
                this.failureMessage.value = Resources.BranchAlreadyInList;
            } else {
                this.failureMessage.value = undefined;
            }
        } else {
            this.branchToAdd.value = undefined;
        }
    };

    private showDialogAdd = () => {
        this.hideDialogAdd();
        this.isDialogAddOpen.value = true;
    };

    private showDialogRemove = (branch: IBranch) => {
        this.branchToRemove.value = branch;
        this.isDialogRemoveOpen.value = true;
    };
}

function isIBranch(x: IBranch | IReadonlyObservableValue<IBranch | undefined>): x is IBranch {
    return (x as IBranch).ref ? true : false;
}

Components.add("ms.vss-code-web.settings-options-searchable-branches-component", SearchableBranches);
